import { Fragment, useState } from 'react';

import { SxProps, Theme, Typography, TypographyOwnProps, Popover, Box } from '@mui/material';

import { BackrsIcon } from './BackrsIcon';
import { appBorders } from '../assets/appBorders';
import { appColors } from '../assets/appColors';
import {
  DATA_DICTIONARY,
  DictionaryRecord,
  DictionaryValues,
  DisplayListType,
} from '../assets/dataDictionary';
import { PanelNugget } from '../molecules/Nugget/PanelNugget';

type HighlightableTextProps = Readonly<{
  text: string;
  style?: SxProps<Theme>;
  textVariant?: TypographyOwnProps['variant'];
  additionalDictionary?: DictionaryRecord;
}>;

export function HighlightableText({
  text,
  style,
  textVariant,
  additionalDictionary,
}: HighlightableTextProps) {
  const combinedDictionary: DictionaryRecord = { ...DATA_DICTIONARY, ...additionalDictionary };

  // Escape special regex characters in the dictionary keys
  const escapedKeys = Object.keys(combinedDictionary).map((key) =>
    key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  );

  // Build the regex pattern without word boundaries
  const regex = new RegExp(`(${escapedKeys.join('|')})`, 'gi');

  // Split the text into parts: dictionary terms and regular text
  const parts = text.split(regex);

  return (
    <Typography component="span" sx={style} variant={textVariant}>
      {parts.map((part, index) => {
        const partKey = part;

        if (partKey in combinedDictionary) {
          // If the part matches a dictionary term, render it as a highlighted word
          return (
            <HighlightedWordWithPopover
              key={index}
              word={part.replace(/{{|}}/g, '')}
              blurb={combinedDictionary[partKey].values}
              title={combinedDictionary[partKey].title?.replace(/{{|}}/g, '')}
              displayListType={combinedDictionary[partKey].displayType}
            />
          );
        }

        // Otherwise, render it as plain text
        return (
          <Typography key={index} component="span">
            {part.replace(/{{|}}/g, '')}
          </Typography>
        );
      })}
    </Typography>
  );
}

type HighlightedWordWithPopoverProps = Readonly<{
  word: string;
  blurb: DictionaryValues;
  title?: string;
  displayListType: DisplayListType;
}>;

const HighlightedWordWithPopover = ({
  word,
  blurb,
  title,
  displayListType,
}: HighlightedWordWithPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'word-popover' : undefined;

  return (
    <>
      <Typography component="span" sx={keywordSx} onClick={handleClick} id={id}>
        {word}
      </Typography>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            ...paperSx,
            ...(displayListType.type === 'nugget' ? nuggetSx : {}),
          } as SxProps<Theme>,
        }}
      >
        <PopoverContent displayListType={displayListType} word={word} blurb={blurb} title={title} />
      </Popover>
    </>
  );
};

const PopoverContent = ({
  displayListType,
  word,
  blurb,
  title,
}: HighlightedWordWithPopoverProps) => {
  if (displayListType.type === 'nugget') {
    // for nuggets, we only want to display the nugget and not the extra content
    return (
      <Box>
        <Box
          onClick={() => window.open(displayListType.onClickUrl, '_blank')}
          sx={nuggetArrowIconSx}
        >
          {/* display an arrow icon in the top right to open the nugget in a new tab */}
          <BackrsIcon name="IconArrowOut" alt="icon" />
        </Box>
        <PanelNugget
          variant={displayListType.panelNuggetVariant}
          // uppercase the first letter of each word in the title
          title={(blurb[0]?.title || '')
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')}
          body={blurb[0]?.description}
        />
      </Box>
    );
  }

  const Content = (): JSX.Element => {
    switch (displayListType.type) {
      case 'bullet':
        return (
          <ul style={listStyle}>
            {blurb.map((item, index) => (
              <li key={index} style={listItemStyle}>
                {item.title ? <span style={listItemTitleStyle}>{item.title}</span> : ''}
                {item.description}
              </li>
            ))}
          </ul>
        );

      case 'number':
        return (
          <ol style={listStyle}>
            {blurb.map((item, index) => (
              <li key={index} style={listItemStyle}>
                {item.title ? <span style={listItemTitleStyle}>{item.title} </span> : ''}
                {item.description}
              </li>
            ))}
          </ol>
        );

      case 'block':
        return (
          <Box>
            {blurb.map((item) => {
              return (
                <Fragment key={item.description}>
                  {item.title ? (
                    <Typography variant="h6" mb={2}>
                      {item.title}
                    </Typography>
                  ) : (
                    ''
                  )}
                  <Typography>{item.description}</Typography>
                </Fragment>
              );
            })}
          </Box>
        );
    }
  };

  return (
    <>
      <Typography variant="h6" sx={{ ...titleSx, fontSize: 17 }}>
        {word
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')}
      </Typography>

      {title && <Typography sx={titleSx}>{title}</Typography>}

      <Content />
    </>
  );
};

const paperSx: SxProps<Theme> = {
  fontSize: '14px',
  padding: 2,
  backgroundColor: appColors.neutral.black,
  color: appColors.neutral.white,
  borderRadius: 4,
  whiteSpace: 'pre-line',
  boxSizing: 'border-box',
  minWidth: '450px',
  maxWidth: '450px',
} as const;

const listStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: '16px',
} as const;

const listItemStyle: React.CSSProperties = {
  marginBottom: '8px',
} as const;

const listItemTitleStyle: React.CSSProperties = {
  fontWeight: 600,
} as const;

const titleSx: SxProps<Theme> = {
  fontWeight: 600,
  lineHeight: 1.3,
  fontSize: '12pt',
  mb: 2,
} as const;

const keywordSx: SxProps<Theme> = {
  textDecoration: 'underline',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.5,
  },
} as const;

const nuggetSx: SxProps<Theme> = {
  maxWidth: '400px',
  backgroundColor: 'white',
  color: 'black',
  padding: 0,
  boxShadow: appBorders.backrsDropShadowBorders.black,
} as const;

const nuggetArrowIconSx: SxProps<Theme> = {
  position: 'absolute',
  top: 20,
  right: 20,
  cursor: 'pointer',
} as const;
